// react
import { useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
// @mui
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import { RHFSwitch, RHFTextField } from 'src/components/hook-form';
// redux
import { setError } from 'src/redux/slices/error';
// utils
import { fNumberInput } from 'src/utils/format-input';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { ConfirmDialog } from '../../../components/custom-dialog';
import { useBoolean } from '../../../hooks/use-boolean';
// types
import { TaxRateDto } from '../../../api';
import { useLocales } from '../../../locales';
import { useDispatch } from '../../../redux/store';
import { editTaxEntity, removeTaxEntity, getTaxRates } from '../../../redux/slices/tax';

// ----------------------------------------------------------------------

type Props = {
  taxRate: TaxRateDto;
  index: number;
  setValue: UseFormSetValue<any>;
};

export default function BookkeepingEditItem({ taxRate, index, setValue }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();

  const param = {
    edit: Boolean(searchParams.get('edit')),
    id: searchParams
      .get('id')
      ?.split(',')
      .map((x) => Number(x)),
  };

  const [accountEditView, setAccountEditView] = useState(
    param.edit && param.id?.includes(taxRate.id ?? -1)
  );

  const popover = usePopover();

  const confirm = useBoolean();

  const { t } = useLocales();

  const dispatch = useDispatch();

  const editVat = (selectedId: number | undefined) => {
    if (selectedId !== undefined && taxRate.accountId !== undefined) {
      dispatch(editTaxEntity({ id: selectedId, accountId: Number(taxRate.accountId) }))
        .then(() => enqueueSnackbar(t('Update success!')))
        .then(() => setAccountEditView(false))
        .catch((error) => dispatch(setError(error)));
    }
  };

  const removeVat = (selectedId: number | undefined) => {
    if (selectedId !== undefined) {
      dispatch(removeTaxEntity(selectedId))
        .then(() => enqueueSnackbar(t('Delete success!')))
        .catch((error) => dispatch(setError(error)))
        .finally(() => dispatch(getTaxRates()));
    }
  };

  const handleEditView = () => {
    setAccountEditView(true);
  };

  const renderPopover = (
    <CustomPopover
      open={popover.open}
      onClose={popover.onClose}
      arrow="right-top"
      sx={{ width: 140 }}
    >
      <MenuItem
        onClick={() => {
          popover.onClose();
          handleEditView();
        }}
      >
        <Iconify icon="solar:pen-bold" />
        {t('Edit')}
      </MenuItem>

      <MenuItem
        onClick={() => {
          popover.onClose();
          removeVat(taxRate.id);
        }}
        sx={{ color: 'error.main' }}
      >
        <Iconify icon="solar:trash-bin-trash-bold" />
        {t('Delete')}
      </MenuItem>
    </CustomPopover>
  );

  const renderConfirm = (
    <ConfirmDialog
      open={confirm.value}
      onClose={confirm.onFalse}
      title={t('Edit VAT account')}
      content={
        <>
          {t(
            'Are you sure you want to change the assosiated Account? This will also effect all already invoiced products.'
          )}
        </>
      }
      action={
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            editVat(taxRate.id);
            confirm.onFalse();
          }}
        >
          {t('Save changes')}
        </Button>
      }
    />
  );

  const renderTaxRate = (
    <Grid size={{ md: 3 }}>
      <RHFTextField
        name={`vat-rate-${taxRate.id}`}
        label={taxRate.name}
        value={taxRate.value}
        disabled
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box component="span" sx={{ color: 'text.disabled' }}>
                %
              </Box>
            </InputAdornment>
          ),
        }}
      />
    </Grid>
  );

  const renderInclusiv = (
    <Grid
      size={{ md: 4 }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {taxRate.inclusive ? (
        <RHFSwitch name="taxRateInclusive" disabled checked label={t('Price includes taxes')} />
      ) : (
        <RHFSwitch name="taxRateInclusive" disabled label={t('Price includes taxes')} />
      )}
    </Grid>
  );

  const renderAccountId = (
    <Grid size={{ md: 3 }}>
      <RHFTextField
        name={`vat-account-${taxRate.id}`}
        value={taxRate.accountId}
        disabled={!accountEditView}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box component="span" sx={{ color: 'text.disabled' }}>
                #
              </Box>
            </InputAdornment>
          ),
        }}
        onChange={(event) =>
          fNumberInput({ name: `list[${index}].accountId`, setValue, value: event.target.value })
        }
      />
    </Grid>
  );

  const renderAction = (
    <Grid
      size={{ md: 2 }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {accountEditView ? (
        <Button size="large" onClick={() => confirm.onTrue()}>
          <SaveIcon />
        </Button>
      ) : (
        <IconButton onClick={popover.onOpen}>
          <Iconify icon="eva:more-vertical-fill" />
        </IconButton>
      )}
    </Grid>
  );

  const renderVat = (
    <Grid container spacing={5}>
      {renderTaxRate}

      {renderAccountId}

      {renderInclusiv}

      {renderAction}
    </Grid>
  );

  return (
    <>
      <Stack
        spacing={1.5}
        sx={{
          p: (theme) => theme.spacing(1),
        }}
      >
        {renderVat}
      </Stack>

      {renderPopover}

      {renderConfirm}
    </>
  );
}
